import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { message } from 'antd';

import { Common } from 'ontraccr-common';
import ScheduleDrawer from '../ScheduleDrawer';
import { getId, refineReminders } from '../scheduleHelpers';
import { createShift, getShifts } from '../state/schedule.actions';
import { parseHexString } from '../../boards/BoardCardUpload/BoardCardUploadHelpers';
import { parseGanttCostCodeKey } from './ganttScheduleHelpers';
import useUpdateCostcodeIdMap from '../../costcodes/useUpdateCostcodeIdMap';
import { COSTCODE_BY_IDS_COLUMNS } from '../../reports/reportHelpers';

export default function GanttScheduleDispatchDrawer({
  visible,
  onClose,
  row = {},
  shiftId,
  isDisplay,
}) {
  const dispatch = useDispatch();

  const shifts = useSelector((state) => state.schedule.shifts) ?? [];
  const shiftMap = useSelector((state) => state.schedule.shiftMap) ?? {};
  const { settings = {} } = useSelector((state) => state.settings.company) ?? {};
  const projects = useSelector((state) => state.projects.projects) ?? [];
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);

  const [lastDownload, setLastDownload] = useState([]);

  const { shiftConnections = [] } = row;

  const rowCostcodeItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    (row.costCodes || []).forEach((token) => {
      const key = typeof token === 'string' ? token : `${token?.phaseId}.${token?.costcodeId}`;
      const { costCodeId } = parseGanttCostCodeKey(key);
      if (costCodeId && !seen.has(costCodeId)) {
        seen.add(costCodeId);
        items.push({ costcodeId: costCodeId });
      }
    });
    return items;
  }, [row.costCodes]);

  useUpdateCostcodeIdMap({
    items: rowCostcodeItems,
    columns: COSTCODE_BY_IDS_COLUMNS.GANTT,
  });

  useEffect(() => {
    if (shiftConnections?.length && shiftId && !shiftMap[shiftId]) {
      dispatch(getShifts(shiftConnections));
    }
  }, [shiftConnections, shiftId, shiftMap]);

  const shift = useMemo(() => {
    if (shiftId) {
      return shiftMap?.[shiftId] ?? {};
    }
    if (!row || Common.isObjectEmpty(row) || !visible) return {};
    const localZone = DateTime.local().zoneName;
    const startTime = DateTime.fromSeconds(row.startDate, { zone: 'utc' })
      .startOf('day')
      .setZone(localZone, { keepLocalTime: true })
      .toMillis();
    const endTime = DateTime.fromSeconds(row.endDate, { zone: 'utc' })
      .endOf('day')
      .setZone(localZone, { keepLocalTime: true })
      .toMillis();
    const color = parseHexString(row.color);
    const project = projects.find((p) => p.id === row.projectId) || {};
    const allowedCode = (row.costCodes || []).find((token) => {
      const [, ccId] = (typeof token === 'string' ? token : `${token.phaseId}.${token.costcodeId}`).split('.');
      const cc = costcodeIdMap[ccId];
      return cc?.canClockIn ?? false;
    }) || '';
    const [phaseIdFirst, costcodeIdFirst] = allowedCode.split('.');
    const isRealPhase = phaseIdFirst
      && phaseIdFirst !== 'unphased'
      && phaseIdFirst !== 'Unphased'
      && phaseIdFirst !== 'global';
    const newShift = {
      title: row.name ?? row.title,
      expectedUsers: row.expectedUsers,
      startTime,
      endTime,
      color,
      users: row.users,
      description: row.notes ?? row.note,
      projectId: row.projectId,
      divisionId: project.divisionId || row.divisionId || null,
      phaseId: isRealPhase ? phaseIdFirst : (row.phaseId || null),
      costcodeId: phaseIdFirst === 'global' && costcodeIdFirst
        ? `global.${costcodeIdFirst}`
        : (costcodeIdFirst || null),
      equipment: row.equipment || [],
      materials: row.materials || [],
    };

    return newShift;
  }, [row, visible, shiftMap, shiftId, costcodeIdMap]);

  const onShiftSave = useCallback(async (newShift) => {
    const { emailNotification, pushNotification } = settings;
    const {
      reminders = [],
      costcodeId,
    } = newShift;
    const splitCostcodeId = costcodeId?.split('.');
    const isSplit = splitCostcodeId?.length === 2;
    const ourCostcodeId = isSplit ? splitCostcodeId[1] : costcodeId;

    const refinedReminders = refineReminders(reminders);
    window.sessionStorage.setItem('lastReminderConfig', JSON.stringify(refinedReminders));

    if (!row.id) {
      message.error('Could not connect task to shift. Please try again.');
      return;
    }

    const toCreate = {
      id: getId(),
      ...newShift,
      reminders: refinedReminders,
      emailNotification,
      pushNotification,
      ganttScheduleRowId: row.id,
      costcodeId: ourCostcodeId,
    };
    if (dispatch(createShift(toCreate))) {
      onClose();
    }
  }, [
    settings,
    row,
  ]);

  return (
    <ScheduleDrawer
      isNew
      visible={visible}
      onSave={onShiftSave}
      onClose={onClose}
      style={{ zIndex: 1001 }}
      lastDownload={lastDownload}
      setLastDownload={setLastDownload}
      shift={shift}
      shifts={shifts}
      shiftMap={shiftMap}
      isDisplay={isDisplay}
      hideCalendar={isDisplay}
      isGanttDispatch
    />
  );
}

GanttScheduleDispatchDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.shape({
    title: PropTypes.string,
    expectedUsers: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      count: PropTypes.number,
    })),
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    color: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
  }),
  shiftId: PropTypes.string,
  isDisplay: PropTypes.bool,
};

GanttScheduleDispatchDrawer.defaultProps = {
  visible: false,
  row: {},
  shiftId: null,
  isDisplay: false,
};
